import {Injectable} from "@angular/core";

import {ApiService} from "app/services/domain/api.service";
import * as Models from "app/services/proxy/model/models";
import {RangeSelectionExtended} from "app/shared/forms/time-range/range-selection-extended";

import {DedupeInterceptorStore, getPathSuffix} from "framework/services/dedupe-interceptor/dedupe-interceptor-store";
import {FilterOptionsExtended} from "app/services/domain/filter-options/filter-options.service";

/**
 * Returns the end date for anomaly API requests, floored to the start of
 * the minute (seconds and milliseconds zeroed) when the time range is
 * dynamic ("to Now").  For fixed / custom ranges the date is returned
 * unchanged.
 *
 * This ensures that concurrent calls within the same minute produce
 * identical payloads so the HTTP-level DedupeInterceptor cache can
 * match them.
 */
export function floorEndDateForDedupe(range: RangeSelectionExtended): Date
{
    const date = range.getMaxAsDate();
    if (range.rangeResolved?.limitToNow)
    {
        const floored = new Date(date.getTime());
        floored.setSeconds(0, 0);
        return floored;
    }
    return date;
}

@Injectable()
export class AnomalyService
{
    isAnomalyForDimension: boolean    = false;
    persistStateForDimension: boolean = false;

    // HTTP-level deduplication TTL for anomaly endpoints (in milliseconds)
    public static readonly ANOMALY_SUMMARY_TTL: number   = 60_000; // 60 seconds
    public static readonly ANOMALY_RESOURCES_TTL: number = 60_000; // 60 seconds
    public static readonly ANOMALY_DIMENSION_TTL: number = 60_000; // 60 seconds
    constructor(private m_api: ApiService,
                dedupeInterceptorRegistry: DedupeInterceptorStore)
    {
        // Register HTTP-level deduplication for cost anomaly endpoints
        const pathPrefix           = "/cost_anomaly/";
        const dedupeTtl            = (method: string,
                                      path: string) =>
        {
            if (method === "POST")
            {
                const suffix = getPathSuffix(path, pathPrefix);
                if (!suffix) return undefined;
                switch (suffix)
                {
                    case "billing_cost_anomaly":
                        return AnomalyService.ANOMALY_SUMMARY_TTL;

                    case "billing_cost_anomaly_resources":
                        return AnomalyService.ANOMALY_RESOURCES_TTL;

                    case "dimension_grouping_rule_view":
                        return AnomalyService.ANOMALY_DIMENSION_TTL;
                }
            }
            return undefined;
        };
        const isInvalidatePOSTPath = (path: string) =>
        {
            const suffix = getPathSuffix(path, pathPrefix);
            if (!suffix) return false;
            // Invalidate cache on mutation operations
            switch (suffix)
            {
                case "reject_anomaly":
                case "dimension_grouping_rule/reject":
                    return true;

                case "billing_cost_anomaly":
                case "billing_cost_anomaly_resources":
                case "dimension_grouping_rule_view":
                    return false;

                default:
                    return false;
            }
        };
        dedupeInterceptorRegistry.registerDedupeTtl(dedupeTtl);
        dedupeInterceptorRegistry.registerInvalidatePOSTPath(pathPrefix, isInvalidatePOSTPath);
    }

    public async fetchAnomalyResource(context: Models.CostAnomalySummaryRequest,
                                      filterOption: FilterOptionsExtended,
                                      currency: Models.Currency): Promise<Models.CostAnomalyResourcesResponse>

    {
        const cloudAccounts = filterOption.filterCloudAccounts(null, null, null, null, null, [currency], null);
        const filter        = Models.CostAnomalyResourcesRequest.newInstance({
                                                                                 start_date         : context.start_date,
                                                                                 end_date           : context.end_date,
                                                                                 filters            : context.filters,
                                                                                 service_account_ids: cloudAccounts.map(id => id.cloud_account_id),
                                                                                 billing_source     : context.billing_source
                                                                             });
        return await this.m_api.costAnomaly.costAnomalyResources(filter);
    }

    public async fetchAnomalySummary(filter: Models.CostAnomalySummaryRequest): Promise<Models.CostAnomalySummaryResponse>
    {
        return await this.m_api.costAnomaly.costAnomalySummary(filter);
    }

    public async fetchDimensionsAnomaly(filter: Models.CostAnomalyGroupingRuleRequest): Promise<Array<Models.CostAnomalyGroupingRuleResponse>>
    {
        return await this.m_api.costAnomaly.getGroupingRuleCostAnomalies(filter);
    }

}
